import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import is from '@sindresorhus/is';
import { useContext } from 'react';
import Maybe from 'true-myth/maybe';

import { ApplicationContext } from '~/shared/context/context';

import type { GlobalSearchJob, GlobalSearchUser } from '../../../../data-contract/global-search';
import { getUserName } from '../../user';
import { TableCellLinkWrapper } from '../common/TableCellLinkWrapper';
import { SkillCell } from '../skills/SkillCell';
import { TableCellWrapper } from '../skills/TableCellWrapper';

type HeadlineRow = {
    readonly area: 'exact' | 'related';
    readonly title: string;
    readonly type: 'headline';
};

export type UserRow = {
    readonly area: 'exact' | 'related';
    readonly skill: GlobalSearchUser;
    readonly type: 'skill';
};

type TableRow = GlobalSearchUser | HeadlineRow | UserRow;

export const useEmployeesColumns = (prioritySkillIds?: readonly string[]): GridColDef<TableRow>[] => {
    const {
        applicationState: { userIsAnonymized },
        translate,
    } = useContext(ApplicationContext);

    return [
        {
            disableColumnMenu: true,
            field: 'employeeName',
            flex: 0.5,
            headerName: translate('pages.global-search.headlines.employee-name'),
            renderCell: ({ row }: GridRenderCellParams<GlobalSearchUser>) => {
                const user = Maybe.just({
                    firstName: row.firstName,
                    lastName: row.lastName,
                });

                const username = getUserName(user, userIsAnonymized);
                const url = `/users/${row.id}`;

                return <TableCellLinkWrapper to={url}>{username}</TableCellLinkWrapper>;
            },
            resizable: false,
            sortable: true,
        },
        {
            disableColumnMenu: true,
            field: 'skills',
            flex: 1,
            headerName: translate('pages.global-search.headlines.employee-skills'),
            renderCell: (params: GridRenderCellParams<GlobalSearchJob>) => {
                if (is.nonEmptyArray(params.row.skills)) {
                    return (
                        <SkillCell
                            defaultVisibleSkillsCount={1}
                            prioritySkillIds={prioritySkillIds}
                            skills={params.row.skills}
                        />
                    );
                }

                return <TableCellWrapper>{translate('common.skills-unavailable')}</TableCellWrapper>;
            },
            sortable: false,
        },
    ];
};
